import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { cleanConditionSyntax } from '../utils/templateParser';
import {
  X,
  FileSignature,
  PenTool,
  GitBranch,
  Edit2,
  MoveHorizontal,
  MoveVertical,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from './icons';

const COMMON_SIGNATURES = [
  'signature.png',
  'Signature.png',
  'VijayalakshmiSign.png',
  'PravinKumarSign.png',
  'AuthorizedSign.png',
  'AdvocateSign.png',
  'BankManagerSign.png',
  'BranchManagerSign.png',
];

export function WhiteBlankImageView({ node, updateAttributes, deleteNode, selected, getPos }) {
  const {
    width = '1.0',
    height = '1.0',
    filename = 'signature.png',
    align = 'left',
    condition = '',
    conditionData = '',
    ruleId = '',
  } = node.attrs;

  const isWhiteBox = (filename || '').toLowerCase() === 'whiteblankimage.png';

  const [wVal, setWVal] = useState(width);
  const [hVal, setHVal] = useState(height);
  const [fnVal, setFnVal] = useState(filename || (isWhiteBox ? 'WhiteBlankImage.png' : 'signature.png'));
  const [alignVal, setAlignVal] = useState(align);
  const initialCond = cleanConditionSyntax(condition, conditionData);
  const [condVal, setCondVal] = useState(initialCond);

  useEffect(() => {
    setWVal(width);
  }, [width]);

  useEffect(() => {
    setHVal(height);
  }, [height]);

  useEffect(() => {
    setFnVal(filename || 'WhiteBlankImage.png');
  }, [filename]);

  useEffect(() => {
    setAlignVal(align || 'left');
  }, [align]);

  useEffect(() => {
    const cleaned = cleanConditionSyntax(condition, conditionData);
    setCondVal(cleaned);
    if (condition && condition !== cleaned && typeof updateAttributes === 'function') {
      updateAttributes({ condition: cleaned });
    }
  }, [condition, conditionData, updateAttributes]);

  const handleWidthChange = (e) => {
    const val = e.target.value;
    setWVal(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      updateAttributes({ width: String(val) });
    }
  };

  const handleHeightChange = (e) => {
    const val = e.target.value;
    setHVal(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      updateAttributes({ height: String(val) });
    }
  };

  const handleFilenameChange = (e) => {
    const val = e.target.value;
    setFnVal(val);
    updateAttributes({ filename: val });
  };

  const handleFilenameBlur = () => {
    let clean = (fnVal || '').trim();
    if (!clean) {
      clean = isWhiteBox ? 'WhiteBlankImage.png' : 'signature.png';
    } else if (!clean.toLowerCase().endsWith('.png')) {
      clean = clean + '.png';
    }
    clean = clean.replace(/\s+/g, '_');
    setFnVal(clean);
    updateAttributes({ filename: clean });
  };

  const handleAlignChange = (newAlign) => {
    setAlignVal(newAlign);
    updateAttributes({ align: newAlign });
  };

  const handleTypeToggle = (type) => {
    if (type === 'whitebox') {
      const newAttrs = {
        filename: 'WhiteBlankImage.png',
      };
      setFnVal('WhiteBlankImage.png');
      updateAttributes(newAttrs);
    } else {
      const targetFn = fnVal.toLowerCase() === 'whiteblankimage.png' ? 'signature.png' : fnVal;
      const newAttrs = {
        filename: targetFn,
      };
      setFnVal(targetFn);
      updateAttributes(newAttrs);
    }
  };

  const handleOpenCondition = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    let parsedData = null;
    if (conditionData) {
      try {
        parsedData = typeof conditionData === 'string' ? JSON.parse(conditionData) : conditionData;
      } catch (err) {}
    } else if (condVal) {
      const cleanField = condVal.replace(/<<|>>/g, '').split('==')[0].trim();
      const cleanVal = (condVal.split('==')[1] || '').trim();
      parsedData = {
        operator: '&&',
        rules: [{ field: cleanField, operator: 'equals', value: cleanVal }],
        label: condVal,
      };
    }

    const pos = typeof getPos === 'function' ? getPos() : null;
    const currentRuleId = ruleId || `sig_${Date.now()}`;
    if (!ruleId) {
      updateAttributes({ ruleId: currentRuleId });
    }

    window.dispatchEvent(
      new CustomEvent('openConditionEdit', {
        detail: {
          sigPos: pos,
          ruleId: currentRuleId,
          data: parsedData,
        },
      })
    );
  };

  const handleRemoveCondition = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCondVal('');
    updateAttributes({
      condition: '',
      conditionData: '',
      ruleId: '',
    });
  };

  // Build live syntax preview string
  const alignPrefix = alignVal === 'right' ? '=> ' : alignVal === 'center' ? '== ' : '';
  let condDisplay = cleanConditionSyntax(condVal || condition, conditionData);
  if (condDisplay) {
    if (!condDisplay.startsWith('[')) condDisplay = `[${condDisplay}]`;
  }
  const fullSyntax = condDisplay
    ? `{${alignPrefix ? alignPrefix : ' '}! ${wVal} ${hVal} ${fnVal} ${condDisplay} }`
    : `${alignPrefix}! ${wVal} ${hVal} ${fnVal}`.trim();

  const justifyAlignment =
    alignVal === 'center' ? 'center' : alignVal === 'right' ? 'flex-end' : 'flex-start';

  return (
    <NodeViewWrapper
      className={`white-blank-image-wrapper ${selected ? 'ProseMirror-selectednode' : ''}`}
      contentEditable={false}
      style={{
        margin: '10px 0',
        display: 'flex',
        justifyContent: justifyAlignment,
        userSelect: 'none',
        width: '100%',
      }}
    >
      <div
        className="white-blank-image-container"
        style={{
          border: selected ? '2px solid #2563eb' : condVal ? '1.5px solid #a855f7' : '1.5px solid #cbd5e1',
          borderRadius: '10px',
          background: '#f8fafc',
          padding: '8px 12px',
          display: 'inline-flex',
          flexDirection: 'column',
          gap: '8px',
          maxWidth: '100%',
          boxShadow: selected
            ? '0 0 0 3px rgba(37,99,235,0.15)'
            : condVal
            ? '0 2px 6px rgba(168,85,247,0.12)'
            : '0 1px 3px rgba(0,0,0,0.06)',
          transition: 'all 0.15s ease',
        }}
      >
        {/* Top Header Row: Mode Switcher & Syntax Badge & Delete */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          {/* Mode Switcher Buttons */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#e2e8f0',
              borderRadius: '6px',
              padding: '2px',
              gap: '2px',
            }}
          >
            <button
              type="button"
              onClick={() => handleTypeToggle('signature')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '3px 8px',
                borderRadius: '5px',
                fontSize: '11px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: !isWhiteBox ? '#ffffff' : 'transparent',
                color: !isWhiteBox ? '#2563eb' : '#64748b',
                boxShadow: !isWhiteBox ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease',
              }}
              title="Signature file (e.g. signature.png)"
            >
              <PenTool size={12} />
              <span>Signature</span>
            </button>
            <button
              type="button"
              onClick={() => handleTypeToggle('whitebox')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '3px 8px',
                borderRadius: '5px',
                fontSize: '11px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: isWhiteBox ? '#ffffff' : 'transparent',
                color: isWhiteBox ? '#0f172a' : '#64748b',
                boxShadow: isWhiteBox ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease',
              }}
              title="White signature box (WhiteBlankImage.png)"
            >
              <FileSignature size={12} />
              <span>White Box</span>
            </button>
          </div>

          {/* Syntax preview badge */}
          <span
            style={{
              fontSize: '11px',
              fontFamily: 'monospace',
              background: condVal ? '#ede9fe' : '#e2e8f0',
              color: condVal ? '#6d28d9' : '#334155',
              padding: '2px 8px',
              borderRadius: '5px',
              border: condVal ? '1px solid #ddd6fe' : '1px solid transparent',
              fontWeight: 600,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title="Generated Template Directive Syntax"
          >
            {fullSyntax}
          </span>

          {/* Delete button */}
          <button
            type="button"
            onClick={deleteNode}
            title="Remove Signature"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              border: 'none',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              borderRadius: '4px',
              marginLeft: 'auto',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ef4444';
              e.currentTarget.style.backgroundColor = '#fee2e2';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#94a3b8';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Controls Row: Name, Size, Alignment & Condition */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          {/* Filename Input (Editable when in Signature mode) */}
          {!isWhiteBox ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '2px 8px',
                fontSize: '12px',
              }}
              title="Signature file name (must end in .png, no spaces)"
            >
              <span style={{ fontWeight: 600, fontSize: '11px', color: '#64748b' }}>Name:</span>
              <input
                type="text"
                list="signature-presets"
                value={fnVal}
                onChange={handleFilenameChange}
                onBlur={handleFilenameBlur}
                placeholder="signature.png"
                style={{
                  width: '160px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '12px',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  color: '#0f172a',
                  background: 'transparent',
                }}
              />
              <datalist id="signature-presets">
                {COMMON_SIGNATURES.map((sig) => (
                  <option key={sig} value={sig} />
                ))}
              </datalist>
            </div>
          ) : (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '2px 8px',
                fontSize: '11px',
                color: '#64748b',
                fontWeight: 600,
              }}
            >
              <span>WhiteBlankImage.png</span>
            </div>
          )}

          {/* Width input */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '2px 6px',
              fontSize: '12px',
              color: '#475569',
            }}
            title="Width (cm/in)"
          >
            <MoveHorizontal size={13} color="#64748b" />
            <span style={{ fontWeight: 600, fontSize: '11px' }}>W:</span>
            <input
              type="number"
              step="0.1"
              min="0.1"
              max="50"
              value={wVal}
              onChange={handleWidthChange}
              style={{
                width: '42px',
                border: 'none',
                outline: 'none',
                fontSize: '12px',
                fontFamily: 'inherit',
                fontWeight: 600,
                color: '#0f172a',
                background: 'transparent',
              }}
            />
          </div>

          {/* Height input */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '2px 6px',
              fontSize: '12px',
              color: '#475569',
            }}
            title="Height (cm/in)"
          >
            <MoveVertical size={13} color="#64748b" />
            <span style={{ fontWeight: 600, fontSize: '11px' }}>H:</span>
            <input
              type="number"
              step="0.1"
              min="0.1"
              max="50"
              value={hVal}
              onChange={handleHeightChange}
              style={{
                width: '42px',
                border: 'none',
                outline: 'none',
                fontSize: '12px',
                fontFamily: 'inherit',
                fontWeight: 600,
                color: '#0f172a',
                background: 'transparent',
              }}
            />
          </div>

          {/* Alignment Controls (Left, Center, Right) */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '2px',
              gap: '2px',
            }}
            title="Alignment: Left (<=), Center (==), Right (=>)"
          >
            <button
              type="button"
              onClick={() => handleAlignChange('left')}
              title="Align Left (<=)"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '22px',
                border: 'none',
                borderRadius: '4px',
                background: alignVal === 'left' ? '#eff6ff' : 'transparent',
                color: alignVal === 'left' ? '#2563eb' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <AlignLeft size={13} />
            </button>
            <button
              type="button"
              onClick={() => handleAlignChange('center')}
              title="Align Center (==)"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '22px',
                border: 'none',
                borderRadius: '4px',
                background: alignVal === 'center' ? '#eff6ff' : 'transparent',
                color: alignVal === 'center' ? '#2563eb' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <AlignCenter size={13} />
            </button>
            <button
              type="button"
              onClick={() => handleAlignChange('right')}
              title="Align Right (=>)"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '22px',
                border: 'none',
                borderRadius: '4px',
                background: alignVal === 'right' ? '#eff6ff' : 'transparent',
                color: alignVal === 'right' ? '#2563eb' : '#64748b',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <AlignRight size={13} />
            </button>
          </div>

          {/* Condition Button or Condition Pill */}
          {condVal ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: '#faf5ff',
                border: '1.5px solid #c084fc',
                borderRadius: '6px',
                padding: '2px 8px',
                fontSize: '11px',
                color: '#6b21a8',
                fontWeight: 600,
              }}
              title="Click edit or remove condition"
            >
              <GitBranch size={13} color="#9333ea" />
              <span
                style={{
                  cursor: 'pointer',
                  maxWidth: '220px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                onClick={handleOpenCondition}
                title="Click to edit condition"
              >
                {condDisplay}
              </span>
              <button
                type="button"
                onClick={handleOpenCondition}
                title="Edit Condition"
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#7e22ce',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Edit2 size={11} />
              </button>
              <button
                type="button"
                onClick={handleRemoveCondition}
                title="Remove Condition"
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#9333ea',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleOpenCondition}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '6px',
                border: '1px dashed #a855f7',
                background: '#faf5ff',
                color: '#7e22ce',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              title="Add condition (e.g. [<<Arbitrator name>> == Ms. V. Vijayalakshmi ])"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3e8ff';
                e.currentTarget.style.borderColor = '#9333ea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#faf5ff';
                e.currentTarget.style.borderColor = '#a855f7';
              }}
            >
              <GitBranch size={13} color="#9333ea" />
              <span>+ Condition</span>
            </button>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

export default WhiteBlankImageView;
